import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableFooter,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { format } from "date-fns";
import { sv } from "date-fns/locale";
import { useState, useMemo } from "react";
import { Link } from "wouter";
import { Textarea } from "@/components/ui/textarea";
import {
  Plus,
  Play,
  Eye,
  Package,
  Users,
  ShoppingCart,
  Clock,
  Lock,
  Pencil,
  Trash2,
  XCircle,
  CheckSquare,
  Square,
  Save,
  StickyNote,
  CalendarClock,
} from "lucide-react";
import { addDays, startOfDay } from "date-fns";
import { Checkbox } from "@/components/ui/checkbox";
import type { OrderWithItems, ProductionSession } from "@shared/schema";
import AddOrderDialog from "@/components/admin/add-order-dialog";
import EditOrderDialog from "@/components/admin/edit-order-dialog";

export default function AdminDashboard() {
  const { toast } = useToast();
  const [addOrderOpen, setAddOrderOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<OrderWithItems | null>(null);
  const [editingOrder, setEditingOrder] = useState<OrderWithItems | null>(null);
  const [deletingOrderId, setDeletingOrderId] = useState<string | null>(null);
  const [selectedOrderIds, setSelectedOrderIds] = useState<Set<string>>(new Set());

  const { data: notesData } = useQuery<{ admin_notes: string }>({
    queryKey: ["/api/admin/notes"],
  });

  const [notes, setNotes] = useState<string>("");
  const [notesLoaded, setNotesLoaded] = useState(false);

  if (notesData && !notesLoaded) {
    setNotes(notesData.admin_notes || "");
    setNotesLoaded(true);
  }

  const saveNotesMutation = useMutation({
    mutationFn: () => apiRequest("POST", "/api/admin/notes", { admin_notes: notes }),
    onSuccess: () => {
      toast({ title: "Anteckningar sparade" });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/notes"] });
    },
    onError: () => {
      toast({ title: "Fel", description: "Kunde inte spara anteckningar.", variant: "destructive" });
    },
  });

  const { data: orders, isLoading: ordersLoading } = useQuery<OrderWithItems[]>({
    queryKey: ["/api/admin/orders"],
  });

  const { data: invoicedOrderIds } = useQuery<string[]>({
    queryKey: ["/api/admin/invoiced-order-ids"],
  });

  const invoicedSet = new Set(invoicedOrderIds || []);

  const { data: stats } = useQuery<{
    totalOrders: number;
    totalClients: number;
    totalProducts: number;
    pendingOrders: number;
  }>({
    queryKey: ["/api/admin/stats"],
  });

  const { data: activeProduction } = useQuery<ProductionSession | null>({
    queryKey: ["/api/admin/production/active"],
  });

  const startProductionMutation = useMutation({
    mutationFn: (orderIds?: string[]) => apiRequest("POST", "/api/admin/production/start", { orderIds }),
    onSuccess: () => {
      toast({
        title: "Produktion startad",
        description: selectedOrderIds.size > 0 
          ? `${selectedOrderIds.size} ordrar är nu låsta och produktion har startat.`
          : "Alla ordrar är nu låsta och produktion har startat.",
      });
      setSelectedOrderIds(new Set());
      queryClient.invalidateQueries({ queryKey: ["/api/admin/orders"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/stats"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/production/active"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/production/totals"] });
    },
    onError: () => {
      toast({
        title: "Fel",
        description: "Kunde inte starta produktion.",
        variant: "destructive",
      });
    },
  });

  const cancelProductionMutation = useMutation({
    mutationFn: () => apiRequest("POST", "/api/admin/production/cancel"),
    onSuccess: () => {
      toast({
        title: "Produktion avbruten",
        description: "Ordrar har låsts upp och kan nu redigeras.",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/orders"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/stats"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/production/active"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/production/totals"] });
    },
    onError: () => {
      toast({
        title: "Fel",
        description: "Kunde inte avbryta produktion.",
        variant: "destructive",
      });
    },
  });

  const toggleOrderSelection = (orderId: string) => {
    setSelectedOrderIds(prev => {
      const next = new Set(prev);
      if (next.has(orderId)) {
        next.delete(orderId);
      } else {
        next.add(orderId);
      }
      return next;
    });
  };

  const deleteOrderMutation = useMutation({
    mutationFn: (orderId: string) => apiRequest("DELETE", `/api/admin/orders/${orderId}`),
    onSuccess: () => {
      toast({ title: "Order raderad!" });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/orders"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/stats"] });
      setDeletingOrderId(null);
    },
    onError: () => {
      toast({
        title: "Fel",
        description: "Kunde inte radera order.",
        variant: "destructive",
      });
    },
  });

  const getStatusBadge = (status: string) => {
    const variants: Record<string, { label: string; className: string }> = {
      pending: { label: "Väntande", className: "bg-amber-500/10 text-amber-600 border-amber-500/20" },
      confirmed: { label: "Bekräftad", className: "bg-blue-500/10 text-blue-600 border-blue-500/20" },
      in_production: { label: "I produktion", className: "bg-purple-500/10 text-purple-600 border-purple-500/20" },
      packed: { label: "Färdig att leverera", className: "bg-teal-500/10 text-teal-600 border-teal-500/20" },
      delivered: { label: "Levererad", className: "bg-emerald-500/10 text-emerald-600 border-emerald-500/20" },
      levererad: { label: "Levererad", className: "bg-emerald-500/10 text-emerald-600 border-emerald-500/20" },
    };
    const variant = variants[status] || { label: status, className: "" };
    return <Badge variant="outline" className={variant.className}>{variant.label}</Badge>;
  };

  const getUnitLabel = (unit: string) => {
    switch (unit) {
      case "pcs": return "st";
      case "bag": return "påse";
      case "box": return "låda";
      default: return unit;
    }
  };

  // Only show orders that haven't started production yet
  const allPendingOrders = orders?.filter(o => o.status === "pending" || o.status === "confirmed") || [];

  // Future orders: scheduledDelivery=true AND delivery date is 2+ days away (not yet time to produce)
  const dayAfterTomorrow = startOfDay(addDays(new Date(), 2));
  const futureOrders = allPendingOrders.filter(o => {
    if (!o.scheduledDelivery || !o.deliveryDate) return false;
    return startOfDay(new Date(o.deliveryDate)) >= dayAfterTomorrow;
  });
  const futureOrderIds = new Set(futureOrders.map(o => o.id));

  // Regular orders: everything else (including scheduled orders whose delivery is tomorrow = ready to produce)
  const pendingOrders = allPendingOrders.filter(o => !futureOrderIds.has(o.id));

  // Split pending orders: today's orders vs tomorrow's scheduled delivery orders
  const tomorrowOrders = pendingOrders.filter(o => o.scheduledDelivery && o.deliveryDate);
  const todayOrders = pendingOrders.filter(o => !o.scheduledDelivery || !o.deliveryDate);

  const selectAllOrders = () => {
    setSelectedOrderIds(new Set(pendingOrders.map(o => o.id)));
  };

  const deselectAllOrders = () => {
    setSelectedOrderIds(new Set());
  };

  const calcTotals = (orderList: typeof pendingOrders) => {
    const totals: Map<string, { name: string; quantity: number; unit: string }> = new Map();
    for (const order of orderList) {
      if (!order.items) continue;
      for (const item of order.items) {
        const productId = item.productId;
        const productName = item.product?.name || "Okänd produkt";
        const unit = item.product?.sellUnit || "pcs";
        if (totals.has(productId)) {
          totals.get(productId)!.quantity += item.quantity;
        } else {
          totals.set(productId, { name: productName, quantity: item.quantity, unit });
        }
      }
    }
    return Array.from(totals.values());
  };

  // Product totals: split between today's and tomorrow's orders
  const todayProductTotals = useMemo(() => calcTotals(todayOrders), [todayOrders]);
  const tomorrowProductTotals = useMemo(() => calcTotals(tomorrowOrders), [tomorrowOrders]);

  const getUnitLabelShort = (unit: string) => {
    switch (unit) {
      case "pcs": return "st";
      case "bag": return "påsar";
      case "box": return "lådor";
      default: return unit;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Adminpanel</h1>
          <p className="text-muted-foreground">Hantera ordrar och produktion</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" onClick={() => setAddOrderOpen(true)} data-testid="button-add-order">
            <Plus className="h-4 w-4 mr-2" />
            Lägg till order
          </Button>
          
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                disabled={!!activeProduction || pendingOrders.length === 0}
                data-testid="button-start-production"
              >
                {activeProduction ? (
                  <>
                    <Lock className="h-4 w-4 mr-2" />
                    Produktion pågår
                  </>
                ) : (
                  <>
                    <Play className="h-4 w-4 mr-2" />
                    Starta produktion
                  </>
                )}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Starta produktion?</AlertDialogTitle>
                <AlertDialogDescription>
                  Detta kommer att låsa valda ordrar. Inga ändringar kan göras efter detta.
                  {selectedOrderIds.size > 0 
                    ? `${selectedOrderIds.size} valda order(er) kommer att inkluderas i produktionen.`
                    : `Alla ${pendingOrders.length} order(er) kommer att inkluderas i produktionen.`
                  }
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Avbryt</AlertDialogCancel>
                <AlertDialogAction
                  onClick={() => startProductionMutation.mutate(
                    selectedOrderIds.size > 0 ? Array.from(selectedOrderIds) : undefined
                  )}
                  disabled={startProductionMutation.isPending}
                >
                  {startProductionMutation.isPending ? "Startar..." : "Starta produktion"}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          {activeProduction && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="destructive" data-testid="button-cancel-production">
                  <XCircle className="h-4 w-4 mr-2" />
                  Avbryt produktion
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Avbryt produktion?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Detta kommer att avsluta produktionen och låsa upp alla ordrar som fortfarande är i produktion. 
                    Ordrar som redan packats påverkas inte. Du kan starta en ny produktion efteråt.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Nej, fortsätt produktion</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={() => cancelProductionMutation.mutate()}
                    disabled={cancelProductionMutation.isPending}
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  >
                    {cancelProductionMutation.isPending ? "Avbryter..." : "Avbryt produktion"}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </div>

      <Card data-testid="card-admin-notes">
        <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
          <div className="flex items-center gap-2">
            <StickyNote className="h-4 w-4 text-muted-foreground" />
            <CardTitle className="text-base">Anteckningar</CardTitle>
          </div>
          <Button
            size="sm"
            onClick={() => saveNotesMutation.mutate()}
            disabled={saveNotesMutation.isPending}
            data-testid="button-save-notes"
          >
            <Save className="h-4 w-4 mr-2" />
            {saveNotesMutation.isPending ? "Sparar..." : "Spara"}
          </Button>
        </CardHeader>
        <CardContent>
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Skriv anteckningar här..."
            className="min-h-[80px] resize-y"
            data-testid="textarea-admin-notes"
          />
        </CardContent>
      </Card>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-4">
              <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <ShoppingCart className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats?.pendingOrders ?? 0}</p>
                <p className="text-sm text-muted-foreground">Väntande ordrar</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-4">
              <div className="h-10 w-10 rounded-lg bg-green-500/10 flex items-center justify-center">
                <Package className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats?.totalOrders ?? 0}</p>
                <p className="text-sm text-muted-foreground">Totalt ordrar</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-4">
              <div className="h-10 w-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                <Users className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats?.totalClients ?? 0}</p>
                <p className="text-sm text-muted-foreground">Kunder</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-4">
              <div className="h-10 w-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
                <Clock className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats?.totalProducts ?? 0}</p>
                <p className="text-sm text-muted-foreground">Produkter</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Senaste ordrar</CardTitle>
          <CardDescription>Översikt över alla inkomna beställningar</CardDescription>
        </CardHeader>
        <CardContent>
          {ordersLoading ? (
            <div className="overflow-x-auto -mx-6 px-6 sm:mx-0 sm:px-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">Välj</TableHead>
                    <TableHead>Kund</TableHead>
                    <TableHead>Belopp</TableHead>
                    <TableHead>Datum</TableHead>
                    <TableHead>Tid</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Åtgärd</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {[1, 2, 3].map((i) => (
                    <TableRow key={i}>
                      <TableCell><Skeleton className="h-4 w-4" /></TableCell>
                      <TableCell><Skeleton className="h-4 w-28" /></TableCell>
                      <TableCell><Skeleton className="h-4 w-16" /></TableCell>
                      <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                      <TableCell><Skeleton className="h-4 w-12" /></TableCell>
                      <TableCell><Skeleton className="h-6 w-24 rounded-full" /></TableCell>
                      <TableCell>
                        <div className="flex items-center justify-end gap-1">
                          <Skeleton className="h-8 w-8 rounded-md" />
                          <Skeleton className="h-8 w-8 rounded-md" />
                          <Skeleton className="h-8 w-8 rounded-md" />
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : pendingOrders.length > 0 ? (
            <>
              <div className="flex items-center gap-2 mb-4 flex-wrap">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={selectAllOrders}
                  disabled={!!activeProduction}
                  data-testid="button-select-all-orders"
                >
                  <CheckSquare className="h-4 w-4 mr-2" />
                  Välj alla
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={deselectAllOrders}
                  disabled={!!activeProduction}
                  data-testid="button-deselect-all-orders"
                >
                  <Square className="h-4 w-4 mr-2" />
                  Avmarkera
                </Button>
                {selectedOrderIds.size > 0 && (
                  <Badge variant="secondary" className="ml-2">
                    {selectedOrderIds.size} ordrar valda
                  </Badge>
                )}
              </div>
              <div className="overflow-x-auto -mx-6 px-6 sm:mx-0 sm:px-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">Välj</TableHead>
                    <TableHead>Kund</TableHead>
                    <TableHead>Belopp</TableHead>
                    <TableHead>Datum</TableHead>
                    <TableHead>Tid</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Åtgärd</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {todayOrders.map((order) => (
                    <TableRow key={order.id} className={selectedOrderIds.has(order.id) ? "bg-primary/5" : ""}>
                      <TableCell>
                        <Checkbox
                          checked={selectedOrderIds.has(order.id)}
                          onCheckedChange={() => toggleOrderSelection(order.id)}
                          disabled={!!activeProduction}
                          data-testid={`checkbox-order-${order.id}`}
                        />
                      </TableCell>
                      <TableCell className="font-medium">
                        {order.client?.name || "Okänd kund"}
                      </TableCell>
                    <TableCell>{order.totalAmount} kr</TableCell>
                    <TableCell>
                      {format(new Date(order.orderDate!), "d MMM yyyy", { locale: sv })}
                    </TableCell>
                    <TableCell>
                      {format(new Date(order.orderDate!), "HH:mm", { locale: sv })}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 flex-wrap">
                        {getStatusBadge(order.status)}
                        {invoicedSet.has(order.id) && (
                          <Badge variant="outline" className="bg-indigo-500/10 text-indigo-600 border-indigo-500/20" data-testid={`badge-invoiced-${order.id}`}>
                            Fakturerad
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => setSelectedOrder(order)}
                          data-testid={`button-view-order-${order.id}`}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        {!order.isLocked && (
                          <>
                            <Button
                              size="icon"
                              variant="ghost"
                              onClick={() => setEditingOrder(order)}
                              data-testid={`button-edit-order-${order.id}`}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <AlertDialog open={deletingOrderId === order.id} onOpenChange={(open) => !open && setDeletingOrderId(null)}>
                              <AlertDialogTrigger asChild>
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  className="text-destructive"
                                  onClick={() => setDeletingOrderId(order.id)}
                                  data-testid={`button-delete-order-${order.id}`}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Radera order?</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    Detta kommer att permanent radera ordern för {order.client?.name}. 
                                    Denna åtgärd kan inte ångras.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Avbryt</AlertDialogCancel>
                                  <AlertDialogAction
                                    onClick={() => deleteOrderMutation.mutate(order.id)}
                                    disabled={deleteOrderMutation.isPending}
                                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                  >
                                    {deleteOrderMutation.isPending ? "Raderar..." : "Radera"}
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {tomorrowOrders.length > 0 && (
                  <>
                    <TableRow className="bg-amber-500/10 hover:bg-amber-500/10">
                      <TableCell colSpan={7} className="py-1.5 px-4">
                        <div className="flex items-center gap-2 text-amber-700 dark:text-amber-400 text-sm font-semibold">
                          <CalendarClock className="h-3.5 w-3.5" />
                          Leverans imorgon — produceras idag
                        </div>
                      </TableCell>
                    </TableRow>
                    {tomorrowOrders.map((order) => (
                      <TableRow key={order.id} className={`bg-amber-500/5 ${selectedOrderIds.has(order.id) ? "bg-amber-500/15" : ""}`}>
                        <TableCell>
                          <Checkbox
                            checked={selectedOrderIds.has(order.id)}
                            onCheckedChange={() => toggleOrderSelection(order.id)}
                            disabled={!!activeProduction}
                            data-testid={`checkbox-order-${order.id}`}
                          />
                        </TableCell>
                        <TableCell className="font-medium">
                          {order.client?.name || "Okänd kund"}
                        </TableCell>
                        <TableCell>{order.totalAmount} kr</TableCell>
                        <TableCell>
                          {format(new Date(order.orderDate!), "d MMM yyyy", { locale: sv })}
                        </TableCell>
                        <TableCell>
                          {format(new Date(order.orderDate!), "HH:mm", { locale: sv })}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1 flex-wrap">
                            {getStatusBadge(order.status)}
                            <Badge variant="outline" className="bg-amber-500/10 text-amber-700 border-amber-500/30 dark:text-amber-400" data-testid={`badge-delivery-date-${order.id}`}>
                              <CalendarClock className="h-3 w-3 mr-1" />
                              {format(new Date(order.deliveryDate!), "d MMM", { locale: sv })}
                            </Badge>
                            {invoicedSet.has(order.id) && (
                              <Badge variant="outline" className="bg-indigo-500/10 text-indigo-600 border-indigo-500/20" data-testid={`badge-invoiced-${order.id}`}>
                                Fakturerad
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              size="icon"
                              variant="ghost"
                              onClick={() => setSelectedOrder(order)}
                              data-testid={`button-view-order-${order.id}`}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            {!order.isLocked && (
                              <>
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  onClick={() => setEditingOrder(order)}
                                  data-testid={`button-edit-order-${order.id}`}
                                >
                                  <Pencil className="h-4 w-4" />
                                </Button>
                                <AlertDialog open={deletingOrderId === order.id} onOpenChange={(open) => !open && setDeletingOrderId(null)}>
                                  <AlertDialogTrigger asChild>
                                    <Button
                                      size="icon"
                                      variant="ghost"
                                      className="text-destructive"
                                      onClick={() => setDeletingOrderId(order.id)}
                                      data-testid={`button-delete-order-${order.id}`}
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  </AlertDialogTrigger>
                                  <AlertDialogContent>
                                    <AlertDialogHeader>
                                      <AlertDialogTitle>Radera order?</AlertDialogTitle>
                                      <AlertDialogDescription>
                                        Detta kommer att permanent radera ordern för {order.client?.name}.
                                        Denna åtgärd kan inte ångras.
                                      </AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <AlertDialogFooter>
                                      <AlertDialogCancel>Avbryt</AlertDialogCancel>
                                      <AlertDialogAction
                                        onClick={() => deleteOrderMutation.mutate(order.id)}
                                        disabled={deleteOrderMutation.isPending}
                                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                      >
                                        {deleteOrderMutation.isPending ? "Raderar..." : "Radera"}
                                      </AlertDialogAction>
                                    </AlertDialogFooter>
                                  </AlertDialogContent>
                                </AlertDialog>
                              </>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </>
                )}
              </TableBody>
              {(todayProductTotals.length > 0 || tomorrowProductTotals.length > 0) && (
                <TableFooter>
                  {todayProductTotals.length > 0 && (
                    <TableRow>
                      <TableCell colSpan={7} className="bg-muted/50">
                        <div className="py-2">
                          <p className="font-semibold mb-2">Produkttotaler — idag</p>
                          <div className="flex flex-wrap gap-3">
                            {todayProductTotals.map((item, index) => (
                              <Badge key={index} variant="secondary" className="text-sm">
                                {item.name}: {item.quantity} {getUnitLabelShort(item.unit)}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                  {tomorrowProductTotals.length > 0 && (
                    <TableRow>
                      <TableCell colSpan={7} className="bg-amber-500/5">
                        <div className="py-2">
                          <p className="font-semibold mb-2 text-amber-700 dark:text-amber-400">Produkttotaler — imorgon leverans</p>
                          <div className="flex flex-wrap gap-3">
                            {tomorrowProductTotals.map((item, index) => (
                              <Badge key={index} variant="outline" className="text-sm bg-amber-500/10 text-amber-700 border-amber-500/30 dark:text-amber-400">
                                {item.name}: {item.quantity} {getUnitLabelShort(item.unit)}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </TableFooter>
              )}
            </Table>
              </div>
            </>
          ) : (
            <div className="py-8 text-center text-muted-foreground">
              Inga ordrar ännu
            </div>
          )}
        </CardContent>
      </Card>

      {futureOrders.length > 0 && (
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <CalendarClock className="h-5 w-5 text-blue-500" />
              <div>
                <CardTitle>Framtida ordrar</CardTitle>
                <CardDescription>Schemalagda ordrar som inte är redo för produktion än</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto -mx-6 px-6 sm:mx-0 sm:px-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Kund</TableHead>
                    <TableHead>Belopp</TableHead>
                    <TableHead>Beställd</TableHead>
                    <TableHead>Leveransdatum</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Åtgärd</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {futureOrders
                    .slice()
                    .sort((a, b) => new Date(a.deliveryDate!).getTime() - new Date(b.deliveryDate!).getTime())
                    .map((order) => (
                    <TableRow key={order.id} className="bg-blue-500/5">
                      <TableCell className="font-medium">
                        {order.client?.name || "Okänd kund"}
                      </TableCell>
                      <TableCell>{order.totalAmount} kr</TableCell>
                      <TableCell>
                        {format(new Date(order.orderDate!), "d MMM yyyy, HH:mm", { locale: sv })}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="bg-blue-500/10 text-blue-600 border-blue-500/30 font-semibold">
                            <CalendarClock className="h-3 w-3 mr-1" />
                            {format(new Date(order.deliveryDate!), "d MMM yyyy", { locale: sv })}
                          </Badge>
                        </div>
                      </TableCell>
                      <TableCell>
                        {getStatusBadge(order.status)}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => setSelectedOrder(order)}
                            data-testid={`button-view-future-order-${order.id}`}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          {!order.isLocked && (
                            <>
                              <Button
                                size="icon"
                                variant="ghost"
                                onClick={() => setEditingOrder(order)}
                                data-testid={`button-edit-future-order-${order.id}`}
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <AlertDialog open={deletingOrderId === order.id} onOpenChange={(open) => !open && setDeletingOrderId(null)}>
                                <AlertDialogTrigger asChild>
                                  <Button
                                    size="icon"
                                    variant="ghost"
                                    className="text-destructive"
                                    onClick={() => setDeletingOrderId(order.id)}
                                    data-testid={`button-delete-future-order-${order.id}`}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Radera order?</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      Detta kommer att permanent radera ordern för {order.client?.name}.
                                      Denna åtgärd kan inte ångras.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Avbryt</AlertDialogCancel>
                                    <AlertDialogAction
                                      onClick={() => deleteOrderMutation.mutate(order.id)}
                                      disabled={deleteOrderMutation.isPending}
                                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                    >
                                      {deleteOrderMutation.isPending ? "Raderar..." : "Radera"}
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      <AddOrderDialog open={addOrderOpen} onOpenChange={setAddOrderOpen} />
      <EditOrderDialog 
        order={editingOrder} 
        open={!!editingOrder} 
        onOpenChange={(open) => !open && setEditingOrder(null)} 
      />

      <Dialog open={!!selectedOrder} onOpenChange={() => setSelectedOrder(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              Order #{selectedOrder?.orderNumber}
            </DialogTitle>
          </DialogHeader>
          {selectedOrder && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">Kund</span>
                  <p className="font-medium">{selectedOrder.client?.name}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Datum</span>
                  <p className="font-medium">
                    {format(new Date(selectedOrder.orderDate!), "d MMMM yyyy, HH:mm", { locale: sv })}
                  </p>
                </div>
                <div>
                  <span className="text-muted-foreground">Status</span>
                  <div className="mt-1">{getStatusBadge(selectedOrder.status)}</div>
                </div>
                <div>
                  <span className="text-muted-foreground">Totalt</span>
                  <p className="font-medium">{selectedOrder.totalAmount} kr</p>
                </div>
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Produkt</TableHead>
                    <TableHead className="text-right">Antal</TableHead>
                    <TableHead className="text-right">Pris</TableHead>
                    <TableHead className="text-right">Summa</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {selectedOrder.items?.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell>{item.product?.name || "Okänd produkt"}</TableCell>
                      <TableCell className="text-right">
                        {item.quantity} {getUnitLabel(item.product?.sellUnit || "pcs")}
                      </TableCell>
                      <TableCell className="text-right">{item.unitPrice} kr</TableCell>
                      <TableCell className="text-right">{item.totalPrice} kr</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {selectedOrder.client && (
                <div className="pt-4 border-t">
                  <h4 className="font-medium mb-2">Kundinformation</h4>
                  <div className="text-sm space-y-1 text-muted-foreground">
                    {selectedOrder.client.address && <p>{selectedOrder.client.address}</p>}
                    {selectedOrder.client.phone && <p>Tel: {selectedOrder.client.phone}</p>}
                    {selectedOrder.client.email && <p>E-post: {selectedOrder.client.email}</p>}
                    {selectedOrder.client.notes && (
                      <p className="mt-2 p-2 bg-muted rounded text-foreground">
                        <strong>Anteckning:</strong> {selectedOrder.client.notes}
                      </p>
                    )}
                  </div>
                </div>
              )}
              <DialogFooter>
                <Button variant="outline" asChild>
                  <Link href={`/admin/delivery-note/${selectedOrder.id}`}>
                    Skriv ut leveranssedel
                  </Link>
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
